import { Button, Divider, Form, Row, Spin, Switch } from "antd";
import Search from "antd/es/input/Search";
import {
  CfdiComplementModel,
  fixDownloadedCfdis,
  getDownloadedCfdis,
} from "api/generated";
import { FormItem } from "common/components/form/FormItem";
import ResponsiveCol from "common/components/form/ResponsiveCol";
import {
  DateFilterType,
  DateRangePicker,
} from "common/components/widgets/datePicker/DateRangePicker";
import { querySerializer } from "common/helpers/queryUtils";
import { useDateRange } from "context/DateRangeContext";
import { usePaginationTable } from "hooks/commons/usePaginationTable";
import { useClient } from "hooks/useClient";
import { useCallback, useEffect, useMemo, useState } from "react";
import { RouteComponentProps, withRouter } from "react-router-dom";
import { CfdiTable, CFDITableRecord } from "./subComplement/cfdi/CFDITable";
import { handleAxiosError } from "common/helpers/generalUtils";
import {
  FilterValue,
  SorterResult,
  TablePaginationConfig,
} from "antd/es/table/interface";
import {
  getPagePermissions,
  INVENTORY_CONTROL_PERMISSION,
} from "security/permissions";
import useLoggedUser from "hooks/useLoggedUser";
import { FileUnknownOutlined } from "@ant-design/icons";
import { useWebSocketCFDIFix } from "hooks/sockets/useWebSocketCFDIFix";

function UnRelatedCFDIsScreen(props: RouteComponentProps) {
  const { client } = useClient();
  const [isLoading, setIsLoading] = useState(false);
  const { rangeTimeValue, setRangeTimeValue, getFormattedDates } =
    useDateRange();
  const { startDate, endDate } = getFormattedDates();
  const [isAssigned, setIsAssigned] = useState<boolean>(false);
  const [cfdiTypes, setCfdiTypes] = useState<
    ("INGRESO" | "EGRESO" | "TRASLADO")[]
  >([]);
  const [rfcReceptorFilter, setRfcReceptorFilter] = useState("");
  const [rfcEmisorFilter, setRfcEmisorFilter] = useState("");

  const { permissions } = useLoggedUser();
  const inventoryControlPermission = getPagePermissions(
    INVENTORY_CONTROL_PERMISSION,
    permissions,
  );

  const pagination = usePaginationTable<CfdiComplementModel>(props, {
    initialSort: ["invoiceDate,DESC"],
    persistInUrl: true,
  });

  const { startNotification, isActive } = useWebSocketCFDIFix({
    onComplete: () => {
      // Recargar la tabla cuando se complete
      reloadTable();
    },
    onError: (error) => {
      handleAxiosError(error, "Error en el proceso de emparejamiento");
    },
  });

  const query = useMemo(
    () =>
      pagination.debouncedFilterText.length > 1
        ? pagination.debouncedFilterText
        : undefined,
    [pagination.debouncedFilterText],
  );

  const reloadTable = useCallback(() => {
    setIsLoading(true);

    getDownloadedCfdis({
      client,
      querySerializer: querySerializer,
      body: {
        startDate,
        endDate,
        searchText: query,
        types: cfdiTypes,
        isAssigned,
        rfcEmisor: rfcEmisorFilter,
        rfcReceptor: rfcReceptorFilter,
      },
      query: {
        pageable: {
          page: pagination.currentPage,
          size: pagination.pageSize,
          sort: pagination.sort,
        },
      },
    })
      .then(({ data }) => {
        pagination.setTotalItems(data?.totalElements ?? 0);
        pagination.setItems(data?.content ?? []);
      })
      .catch((err) => handleAxiosError(err, "No fue posible obtener CFDIs"))
      .finally(() => {
        setIsLoading(false);
      });
  }, [
    startDate,
    endDate,
    client,
    query,
    isAssigned,
    cfdiTypes,
    pagination.currentPage,
    pagination.pageSize,
    rfcEmisorFilter,
    rfcReceptorFilter,
    pagination.sort,
  ]);

  // useEffect principal
  useEffect(() => {
    reloadTable();
  }, [reloadTable]);

  function handleTableChange(
    _pagination: TablePaginationConfig,
    filters: Record<string, FilterValue | null>,
    sorter: SorterResult<CFDITableRecord> | SorterResult<CFDITableRecord>[],
  ) {
    // Manejar filtro de tipos de CFDI (si existe)
    const cfdiTypeFilter = filters.cfdiType;
    if (cfdiTypeFilter) {
      setCfdiTypes(cfdiTypeFilter as ("INGRESO" | "EGRESO" | "TRASLADO")[]);
    } else {
      setCfdiTypes([]);
    }

    // Manejar ordenamiento
    if (!Array.isArray(sorter) && sorter.field && sorter.order) {
      const { field, order } = sorter;
      const direction = order === "ascend" ? "ASC" : "DESC";

      // Mapeo de campos de ordenamiento
      const sortMapping: Record<string, string> = {
        fiscalId: "fiscalId",
        invoiceDate: "invoiceDate",
        rfcReceptor: "rfcReceptor",
        nameReceptor: "nameReceptor",
        rfcEmisor: "rfcEmisor",
        nameEmisor: "nameEmisor",
        invoiceSerie: "invoiceSerie",
        invoiceFolio: "invoiceFolio",
        description: "description",
        permission: "permission",
      };

      const sortField = sortMapping[field as string] || "invoiceDate";

      pagination.setSort([`${sortField},${direction}`]);
    } else {
      // Reset al ordenamiento por defecto si no hay ordenamiento activo
      pagination.setSort(["invoiceDate,DESC"]);
    }
  }

  function handleButtonFix() {
    fixDownloadedCfdis({
      client,
      body: {
        startDate,
        endDate,
        searchText: query,
        types: cfdiTypes,
        isAssigned,
        rfcEmisor: rfcEmisorFilter,
        rfcReceptor: rfcReceptorFilter,
      },
    })
      .then(({ data }) => {
        if (data) {
          startNotification(data);
        }
      })
      .catch((err) => handleAxiosError(err, "No fue posible emparejar CFDIs"))
      .finally(() => {
        setIsLoading(false);
      });
  }

  function handleRfcFilters(
    rfcType: "rfcEmisor" | "rfcReceptor",
    rfcValue: string,
  ) {
    if (rfcType === "rfcEmisor") {
      setRfcEmisorFilter(rfcValue);
    } else {
      setRfcReceptorFilter(rfcValue);
    }
  }

  return (
    <Spin size="large" tip="Cargando..." spinning={isLoading || isActive}>
      <Form layout="vertical">
        <Row gutter={16} align="bottom">
          <ResponsiveCol>
            <FormItem label="Seleccionar periodo">
              <DateRangePicker
                config={{
                  allowedTypes: [
                    DateFilterType.DAY,
                    DateFilterType.MONTH,
                    DateFilterType.RANGE,
                  ],
                  defaultType: DateFilterType.MONTH,
                  showQuickFilters: true,
                }}
                rangeTimeValue={rangeTimeValue}
                setRangeTimeValue={(value) => {
                  pagination.resetPagination();
                  setRangeTimeValue(value);
                }}
              />
            </FormItem>
          </ResponsiveCol>

          <ResponsiveCol>
            <FormItem label="Búsqueda por Serie, Folio, UUID SAT, Descripción">
              <Search
                onChange={(event) => {
                  pagination.resetPagination();
                  pagination.setFilterText(event.target.value);
                }}
                value={pagination.filterText}
              />
            </FormItem>
          </ResponsiveCol>

          <ResponsiveCol size="xs">
            <FormItem label="Mostrar CFDIs Asociados">
              <Switch
                style={{ float: "right" }}
                checkedChildren="CFDIs Asocinados"
                unCheckedChildren="CFDIs sin Asociar"
                value={isAssigned}
                onChange={(checked) => {
                  setIsAssigned(checked);
                }}
              />
            </FormItem>
          </ResponsiveCol>

          <ResponsiveCol size="xl">
            <FormItem>
              <Button
                type="primary"
                icon={<FileUnknownOutlined />}
                style={{ width: "100%" }}
                onClick={() => {
                  handleButtonFix();
                }}
                disabled={
                  !inventoryControlPermission ||
                  pagination.totalItems === 0 ||
                  isAssigned
                }
              >
                Asociar Automáticamente
              </Button>
            </FormItem>
          </ResponsiveCol>
        </Row>
      </Form>

      <CfdiTable
        data={pagination.items}
        pagination={pagination}
        hideActions={true}
        handleTableChange={handleTableChange}
        handleRfcFilters={handleRfcFilters}
        rfcEmisorFilter={rfcEmisorFilter}
        rfcReceptorFilter={rfcReceptorFilter}
      />
      <Divider />
    </Spin>
  );
}

export default withRouter(UnRelatedCFDIsScreen);
